import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Star, Image as ImageIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { API } from './constants';
import { AssetPickerDialog } from './AssetPickerDialog';

const PORTFOLIO_CATEGORIES = ['Wedding', 'Corporate', 'Gala', 'Private', 'Social'];

const EMPTY_FORM = {
  title: '', category: 'Wedding', location: '', event_date: '',
  description: '', image_url: '', media_type: 'image', poster_url: '',
  featured: false, published: true,
};

export const PortfolioTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portfolio?published_only=false`);
      setItems(res.data);
    } catch {
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
  };

  const openNew = () => { resetForm(); setShowModal(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      category: item.category,
      location: item.location || '',
      event_date: item.event_date || '',
      description: item.description || '',
      image_url: item.image_url,
      media_type: item.media_type || 'image',
      poster_url: item.poster_url || '',
      featured: !!item.featured,
      published: item.published !== false,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error('Image is required');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/portfolio/${editing.item_id}`, form, { withCredentials: true });
        toast.success('Portfolio item updated');
      } else {
        await axios.post(`${API}/portfolio`, form, { withCredentials: true });
        toast.success('Portfolio item created');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/portfolio/${item.item_id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchItems();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div data-testid="admin-portfolio-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl text-white mb-2">Portfolio</h2>
          <p className="text-white/50 text-sm">Curate the public portfolio gallery.</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C6A86A] transition-colors"
          data-testid="portfolio-new-btn"
        >
          <Plus className="w-4 h-4" /> New Item
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-white/40 text-sm border border-dashed border-white/10">
          No portfolio items yet. Click &ldquo;New Item&rdquo; to add your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div
              key={item.item_id}
              className="bg-[#0a0a0a] border border-white/5 overflow-hidden group"
              data-testid={`portfolio-row-${item.item_id}`}
            >
              <div className="aspect-[4/3] bg-black relative">
                <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                {item.featured && (
                  <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] uppercase tracking-widest text-black bg-[#D4AF37] px-2 py-1">
                    <Star className="w-3 h-3 fill-black" /> Featured
                  </span>
                )}
                {!item.published && (
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-white/70 bg-black/70 border border-white/20 px-2 py-1">
                    Draft
                  </span>
                )}
              </div>
              <div className="p-5">
                <p className="text-[#D4AF37] text-[10px] uppercase tracking-widest mb-1">{item.category}</p>
                <h3 className="font-heading text-white text-lg mb-1 truncate">{item.title}</h3>
                {item.location && <p className="text-white/50 text-xs mb-3">{item.location}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => openEdit(item)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] border border-white/10"
                    data-testid={`portfolio-edit-${item.item_id}`}
                  >
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-red-400/80 hover:text-red-400 text-xs uppercase tracking-widest border border-red-400/20 hover:border-red-400/50"
                    data-testid={`portfolio-delete-${item.item_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={(v) => { if (!v) { setShowModal(false); resetForm(); } }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">
              {editing ? 'Edit Portfolio Item' : 'New Portfolio Item'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5 mt-4">
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Title *</label>
              <input
                type="text" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                data-testid="portfolio-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Category *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-[#050505] border border-white/20 py-3 px-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  data-testid="portfolio-category-input"
                >
                  {PORTFOLIO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Location</label>
                <input
                  type="text" value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="Nashville, TN"
                />
              </div>
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Date</label>
              <input
                type="text" value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                placeholder="October 2025"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Media Type</label>
              <div className="flex items-center gap-3">
                {['image', 'video'].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, media_type: t })}
                    className={`px-4 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
                      form.media_type === t
                        ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                        : 'bg-[#0a0a0a] text-white/70 border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`portfolio-media-type-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">
                {form.media_type === 'video' ? 'Video URL *' : 'Image URL *'}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="url" required value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="flex-1 bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder={form.media_type === 'video' ? 'https://... .mp4/.webm or pick from library' : 'https://... or pick from library'}
                  data-testid="portfolio-image-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] border border-white/10 whitespace-nowrap"
                  data-testid="portfolio-pick-asset-btn"
                >
                  <ImageIcon className="w-3.5 h-3.5" /> From Library
                </button>
              </div>
              {form.image_url && form.media_type === 'image' && (
                <img
                  src={form.image_url} alt="Preview"
                  className="mt-3 max-h-48 object-contain border border-white/10"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e) => { e.target.style.display = 'block'; }}
                />
              )}
              {form.image_url && form.media_type === 'video' && (
                <video
                  src={form.image_url}
                  controls
                  poster={form.poster_url || undefined}
                  className="mt-3 max-h-48 border border-white/10 bg-black"
                />
              )}
            </div>
            {form.media_type === 'video' && (
              <div>
                <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">
                  Poster Image URL <span className="text-white/30 normal-case">(optional thumbnail shown before play)</span>
                </label>
                <input
                  type="url" value={form.poster_url}
                  onChange={(e) => setForm({ ...form, poster_url: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="https://..."
                  data-testid="portfolio-poster-input"
                />
              </div>
            )}
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-[#D4AF37] resize-none"
                placeholder="Short, evocative description for the lightbox."
              />
            </div>
            <div className="flex items-center gap-8">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox" checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="w-4 h-4 accent-[#D4AF37]"
                />
                <span className="text-white/70 text-sm">Featured</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox" checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4 accent-[#D4AF37]"
                />
                <span className="text-white/70 text-sm">{form.published ? 'Published' : 'Draft'}</span>
              </label>
            </div>
            <button
              type="submit"
              className="w-full py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C6A86A] transition-colors"
              data-testid="portfolio-save-btn"
            >
              {editing ? 'Update Item' : 'Create Item'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AssetPickerDialog
        open={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(url, meta = {}) => {
          setForm((f) => ({
            ...f,
            image_url: url,
            // If the picked asset is a video, switch media_type and prefill the poster
            ...(meta.isVideo ? { media_type: 'video', poster_url: meta.posterUrl || f.poster_url } : {}),
          }));
          setShowPicker(false);
        }}
        mediaFilter={form.media_type}
      />
    </div>
  );
};
